import { DataTableQueryContextProvider } from "@/components/data-table/DataTableQueryContext";
import {
  RowSelectionContext,
  RowSelectionStore,
} from "@/components/data-table/context/RowSelectionContext";
import { QueryTable } from "@/components/query-table/QueryTable";
import { useProjectTypesQuery } from "@/modules/projectTypes/hooks/useProjectTypesQuery";
import { readProjectType } from "@/schemas/projectType";
import { ReadProjectType } from "@/types/projectType";
import { FilterType } from "@/lib/filters";
import { memo, useMemo } from "react";
import { EnumFilter } from "@/components/data-table/components/EnumFilter";
import { PROJECT_DEPENDS_ON } from "@/schemas/commonFields/projectDependsOn";
import { Row } from "node_modules/@tanstack/table-core/build/lib/types";
import { useTranslation } from "react-i18next";

export interface DefaultStoreProjectAssigneesCreatorProjectTypesProps {
  store: RowSelectionStore;
}

const DefaultStoreProjectAssigneesCreatorProjectTypesComponent = ({
  store,
}: DefaultStoreProjectAssigneesCreatorProjectTypesProps) => {
  const { t } = useTranslation();

  const contextValue = useMemo(() => ({ store }), [store]);

  const readProjectTypeWithoutAvailableForStructure = readProjectType
    .unwrap()
    .omit({
      available_for_structure: true,
      services_and_goods: true,
      services_and_goods_code: true,
    })
    .readonly();

  const { query: projectTypesQuery, options: projectTypesOptions } =
    useProjectTypesQuery({
      name: "defaultStoreProjectAssigneesProjectTypes",
      filters: [
        {
          id: "available_for_structure",
          value: {
            filter: FilterType.EqualsString,
            value: "true",
          },
        },
      ],
      defaultSorting: { id: "project_depends_on", desc: false },
    });

  return (
    <RowSelectionContext.Provider value={contextValue}>
      <DataTableQueryContextProvider value={{ query: projectTypesQuery }}>
        <QueryTable<ReadProjectType>
          schema={readProjectTypeWithoutAvailableForStructure}
          name="defaultStoreProjectAssignees"
          query={projectTypesQuery}
          options={projectTypesOptions}
          tableFeatures={{
            enableRowSelection: true,
            enableSelectAll: true,
          }}
          columnOverrides={{
            project_depends_on: {
              id: "project_depends_on",
              cell: ({ row }: { row: Row<ReadProjectType> }) =>
                t(
                  `projectTypes.enum.project_depends_on.${row.original.project_depends_on}`,
                ),
              meta: {
                filter: {
                  component: EnumFilter,
                  props: {
                    options: PROJECT_DEPENDS_ON,
                    translationKey: "projectTypes.enum.project_depends_on",
                  },
                },
              },
            },
          }}
        >
          <div className="flex flex-row items-center gap-2">
            <QueryTable.Menubar className="grow">
              <QueryTable.Menubar.Columns />
              <QueryTable.Menubar.Reset />
            </QueryTable.Menubar>
          </div>
          <div className="flex flex-row grow gap-2 overflow-hidden">
            <QueryTable.Content className="overflow-x-auto flex-grow">
              <QueryTable.Status>
                <QueryTable.SelectedRowsStatus />
              </QueryTable.Status>
            </QueryTable.Content>
          </div>
        </QueryTable>
      </DataTableQueryContextProvider>
    </RowSelectionContext.Provider>
  );
};

export const DefaultStoreProjectAssigneesCreatorProjectTypes = memo(
  DefaultStoreProjectAssigneesCreatorProjectTypesComponent,
);
